/**
 * OneSignal Service
 * 
 * Handles all OneSignal Web Push Notification functionality:
 * - Initialization (via CDN)
 * - Permission requests
 * - Player ID retrieval
 * - Backend registration
 * - User ID mapping (external user ID)
 * - Notification click handlers
 * 
 * Uses CDN approach for better compatibility and simpler implementation
 */

class OneSignalService {
  constructor() {
    this.initialized = false;
    this.playerId = null;
    this.appId = process.env.REACT_APP_ONESIGNAL_APP_ID || 'f76bd786-fe29-4e03-87bd-cc07c8c5fff0';
  }

  /**
   * Check if notifications are supported and provide guidance
   */
  checkNotificationSupport() {
    const info = {
      supported: false,
      browser: '',
      os: '',
      issues: [],
      recommendations: []
    };

    // Detect browser
    const userAgent = navigator.userAgent;
    if (/chrome/i.test(userAgent) && !/edge/i.test(userAgent)) {
      info.browser = 'Chrome';
    } else if (/safari/i.test(userAgent) && !/chrome/i.test(userAgent)) {
      info.browser = 'Safari';
    } else if (/firefox/i.test(userAgent)) {
      info.browser = 'Firefox';
    } else if (/edge/i.test(userAgent)) {
      info.browser = 'Edge';
    }

    // Detect OS
    if (navigator.platform.toUpperCase().indexOf('MAC') >= 0) {
      info.os = 'macOS';
    } else if (navigator.platform.toUpperCase().indexOf('WIN') >= 0) {
      info.os = 'Windows';
    }

    // Check notification API support
    if (!('Notification' in window)) {
      info.issues.push('Browser does not support notifications');
      info.recommendations.push('Please use a modern browser (Chrome, Firefox, Safari, Edge)');
      return info;
    }

    // Check service worker support
    if (!('serviceWorker' in navigator)) {
      info.issues.push('Service Workers not supported');
      info.recommendations.push('Update your browser to the latest version');
      return info;
    }

    // Check HTTPS (required for service workers)
    if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
      info.issues.push('HTTPS required for notifications');
      info.recommendations.push('Access the site via HTTPS');
    }

    // macOS Safari specific checks
    if (info.os === 'macOS' && info.browser === 'Safari') {
      info.recommendations.push('Enable notifications in System Preferences → Notifications → Safari');
      info.recommendations.push('Make sure Safari has permission to send notifications');
      info.recommendations.push('Try Chrome or Firefox if Safari notifications don\'t work');
    }

    // Check current permission
    if (Notification.permission === 'denied') {
      info.issues.push('Notifications blocked by user');
      if (info.os === 'macOS') {
        info.recommendations.push('Go to System Preferences → Notifications → ' + info.browser);
        info.recommendations.push('Enable "Allow Notifications"');
      } else {
        info.recommendations.push('Click the lock icon in address bar → Site Settings → Notifications → Allow');
      }
    }

    info.supported = info.issues.length === 0 || Notification.permission !== 'denied';
    
    return info;
  }

  /**
   * Load OneSignal SDK from CDN
   */
  async loadOneSignalSDK() {
    return new Promise((resolve, reject) => {
      if (window.OneSignal) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js';
      script.async = true;
      script.onload = () => {
        resolve();
      };
      script.onerror = () => {
        reject(new Error('Failed to load OneSignal SDK'));
      };
      document.head.appendChild(script);
    });
  }

  /**
   * Initialize OneSignal SDK
   * Should be called once at app startup
   */
  async initialize() {
    if (this.initialized) {
      return true;
    }

    try {
      const host = window.location.hostname;
      const allowLocalhost = process.env.REACT_APP_ENABLE_ONESIGNAL_LOCALHOST === 'true';
      const allowedHosts = ['phonebookplus.app', 'www.phonebookplus.app'];
      if (allowLocalhost) {
        allowedHosts.push('localhost', '127.0.0.1');
      }
      if (!allowedHosts.includes(host)) {
        return false;
      }

      // Check if we're on macOS Safari
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      
      if (isMac && isSafari) {
        }

      // Load SDK first
      await this.loadOneSignalSDK();

      // Wait a moment for SDK to be ready (longer for Safari)
      await new Promise(resolve => setTimeout(resolve, isSafari ? 1000 : 500));

      if (!window.OneSignal) {
        throw new Error('OneSignal SDK not loaded properly');
      }

      // Initialize OneSignal with Safari-friendly settings
      window.OneSignal.init({
        appId: this.appId,
        safari_web_id: 'web.onesignal.auto.f76bd786-fe29-4e03-87bd-cc07c8c5fff0', // Required for Safari
        notifyButton: {
          enable: false,
        },
        serviceWorkerParam: {
          scope: '/'
        },
        serviceWorkerPath: 'OneSignalSDKWorker.js',
        allowLocalhostAsSecureOrigin: process.env.NODE_ENV === 'development',
        autoRegister: true, // Auto-register service worker
        autoResubscribe: true, // Re-subscribe if user clears data
        persistNotification: true, // Keep notifications visible (important for macOS)
      });

      this.initialized = true;
      // Set up notification click handler
      this.setupNotificationClickHandler();

      // Try to get player ID
      await this.getPlayerId();

      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Request notification permission from the user
   * Enhanced for macOS Safari compatibility
   */
  async requestPermission() {
    if (!this.initialized) {
      return false;
    }

    try {
      // Check current permission state
      const currentPermission = await window.OneSignal.Notifications.permission;
      if (currentPermission === true) {
        return true;
      }

      // For Safari, use native prompt instead of slidedown
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      
      if (isSafari) {
        // Safari works better with direct permission request
        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
          // Manually trigger OneSignal to recognize the permission
          await window.OneSignal.Notifications.setDefaultUrl(window.location.origin);
          return true;
        }
        return false;
      } else {
        await window.OneSignal.Slidedown.promptPush();
        return true;
      }
    } catch (error) {
      // Fallback: try native browser notification API
      try {
        if ('Notification' in window && Notification.permission === 'default') {
          const permission = await Notification.requestPermission();
          return permission === 'granted';
        }
      } catch (fallbackError) {
        }
      
      return false;
    }
  }

  /**
   * Get OneSignal player ID (subscription ID)
   * This is the key method to get the player_id
   */
  async getPlayerId() {
    if (!this.initialized || !window.OneSignal) {
      return null;
    }

    try {
      // Wait a bit for OneSignal to fully initialize
      await new Promise(resolve => setTimeout(resolve, 500));

      // Method 1: Try getting via User.PushSubscription.id (v16 SDK)
      if (window.OneSignal.User && window.OneSignal.User.PushSubscription) {
        const pushSubscription = window.OneSignal.User.PushSubscription.id;
        if (pushSubscription) {
          this.playerId = pushSubscription;
          return pushSubscription;
        }
      }

      // Method 2: Try getting via Notifications.permission (check if subscribed)
      if (window.OneSignal.Notifications) {
        const permission = window.OneSignal.Notifications.permission;
        if (permission === true) {
          // User has granted permission, try again
          await new Promise(resolve => setTimeout(resolve, 1000));
          const pushId = window.OneSignal.User?.PushSubscription?.id;
          if (pushId) {
            this.playerId = pushId;
            return pushId;
          }
        }
      }

      // If user hasn't granted permission yet
      // Listen for subscription change
      if (!this.subscriptionListenerSetup) {
        window.OneSignal.User?.PushSubscription?.addEventListener('change', async () => {
          const newId = window.OneSignal.User?.PushSubscription?.id;
          if (newId) {
            this.playerId = newId;
            // Register with backend if auth token available
            const authToken = localStorage.getItem('jwt_token');
            if (authToken) {
              await this.registerWithBackend(newId, authToken);
            }
          }
        });
        this.subscriptionListenerSetup = true;
      }

      return null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Register player ID with backend
   */
  async registerWithBackend(playerId, authToken) {
    if (!playerId || !authToken) {
      return false;
    }

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://your-api.com';
      const response = await fetch(`${apiUrl}/notifications/onesignal/register`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          player_id: playerId,
        }),
      });

      const data = await response.json();

      if (data.status === '1' || response.ok) {
        return true;
      } else {
        return false;
      }
    } catch (error) {
      return false;
    }
  }

  /**
   * Associate the logged-in user with their OneSignal subscription
   * This enables targeting notifications to specific users
   * 
   * @param {string} userId - Unique user identifier from your backend
   */
  async setExternalUserId(userId) {
    if (!this.initialized || !window.OneSignal) {
      return false;
    }

    if (!userId) {
      return false;
    }

    try {
      await window.OneSignal.login(userId.toString());
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Remove the external user ID association
   * Call this on user logout
   */
  async removeExternalUserId() {
    if (!this.initialized || !window.OneSignal) {
      return;
    }

    try {
      await window.OneSignal.logout();
    } catch (error) {
      // ignore logout errors
    }
  }

  /**
   * Set up handler for notification click events
   */
  setupNotificationClickHandler() {
    try {
      window.OneSignal.Notifications.addEventListener('click', (event) => {
        // Extract data from notification
        const data = event.notification?.additionalData;
        
        if (!data) {
          return;
        }

        // Handle navigation based on notification type
        this.handleNotificationNavigation(data);
      });

    } catch (error) {
      // ignore listener setup errors
    }
  }

  /**
   * Handle navigation when notification is clicked
   */
  handleNotificationNavigation(data) {
    const { type, userId, conversationId, isGroup, groupId } = data;

    try {
      // Navigate based on notification type
      if (type === 'chat_message') {
        if (isGroup && groupId) {
          window.location.href = `/group-chat?groupId=${groupId}`;
          } else if (userId) {
          window.location.href = `/chat?userId=${userId}`;
          }
      } else {
        window.location.href = '/contact';
        }
    } catch (error) {
      }
  }

  /**
   * Get the current subscription state
   */
  async isSubscribed() {
    if (!this.initialized || !window.OneSignal) {
      return false;
    }

    try {
      const permission = await window.OneSignal.Notifications.permissionNative;
      return permission === 'granted';
    } catch (error) {
      return false;
    }
  }
}

// Export singleton instance
const oneSignalService = new OneSignalService();
export default oneSignalService;
